<script setup lang="ts">
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
</script>

<template>
  <div :class="$style.pokerPromoCard">
    <div :class="$style.imageSection">
      <img
        alt="Poker Table"
        :src="getAssetsUrlForCDN('/assets/images/poker-table.png')"
      />
    </div>

    <section :class="$style.contentSection">
      <div :class="$style.promoSection">
        <p :class="$style.promoText">
          {{ $t('poker.promo_text') }}
          <span :class="$style.highlight">
            {{ $t('poker.promo_text_highlight') }}
          </span>
        </p>

        <section :class="$style.playSection">
          <NuxtIcon
            v-for="i in 2"
            :key="i"
            :class="$style.angleDown"
            :name="'poker/angle-down'"
          />

          <section :class="$style.buttonSection">
            <NuxtIcon
              v-for="i in 3"
              :key="i"
              :class="$style.angleSide"
              :name="'poker/angle-left'"
            />

            <NuxtLink
              v-if="isAuthenticated"
              :class="$style.playButton"
              to="/poker/play"
            >
              {{ $t('poker.play_now') }}
            </NuxtLink>

            <button
              v-else
              :class="$style.playButton"
              @click="openLogin()"
            >
              {{ $t('poker.play_now') }}
            </button>

            <NuxtIcon
              v-for="i in 3"
              :key="i"
              :class="$style.angleSide"
              :name="'poker/angle-left'"
            />
          </section>
        </section>
      </div>

      <div :class="$style.advantagesSection">
        <h2>{{ $t('poker.advantages') }}</h2>

        <div :class="$style.advantagesGrid">
          <section>
            <span :class="$style.icon">💰</span>
            <p>{{ $t('poker.payments') }}</p>
          </section>

          <section>
            <span :class="$style.icon">🏆</span>
            <p>{{ $t('poker.tournament') }}</p>
          </section>

          <section>
            <span :class="$style.icon">👑</span>
            <p>{{ $t('poker.status') }}</p>
          </section>

          <section>
            <span :class="$style.icon">📱</span>
            <p>{{ $t('poker.devices') }}</p>
          </section>
        </div>
      </div>
    </section>
  </div>
</template>

<style module lang="scss">
.pokerPromoCard {
  @apply relative w-full py-6 lg:py-8 rounded-lg overflow-hidden text-white text-center shadow-lg lg:flex lg:items-center lg:justify-between px-0 lg:px-8 bg-[#4a00e0];

  &:before {
    @apply bg-gradient-to-t from-black/10 to-white/30 content-[''] absolute top-0 left-0 right-0 bottom-0;
  }

  .imageSection {
    @apply relative p-2 w-full;

    img {
      @apply mx-auto w-96 lg:w-full;
    }
  }

  .contentSection {
    @apply w-full pl-0 lg:pl-8;

    .promoSection {
      @apply relative;

      .promoText {
        @apply text-2xl font-bold px-6;

        .highlight {
          @apply text-[#ffcc00];
        }
      }

      .playSection {
        @apply relative mt-4;

        .angleDown {
          @apply relative block mx-auto -mb-4 first:-mb-6 text-[#4a00e0];
          animation: angleDown 2s infinite steps(2, end);

          svg {
            @apply w-10 h-10 mx-auto;
          }

          &:nth-child(1) {
            opacity: 0.5;
            animation-delay: 1s;
          }

          @keyframes angleDown {
            0% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
        }

        .buttonSection {
          @apply relative flex items-center justify-center;
          .angleSide {
            @apply relative w-3.5 odd:-mx-1;
            animation: angleSide 1.5s infinite ease-in-out;

            svg {
              @apply w-5 h-5;
            }

            &:nth-child(-n + 3) {
              @apply rotate-180;
            }
            &:nth-child(1) {
              animation-delay: 0.1s;
            }
            &:nth-child(2) {
              animation-delay: 0.2s;
            }
            &:nth-child(3) {
              animation-delay: 0.3s;
            }
            &:nth-child(4) {
              animation-delay: 0.3s;
            }
            &:nth-child(5) {
              animation-delay: 0.2s;
            }
            &:nth-child(6) {
              animation-delay: 0.1s;
            }

            @keyframes angleSide {
              0% {
                opacity: 1;
              }
              16% {
                opacity: 0.6;
              }
              33% {
                opacity: 0.9;
              }
              50% {
                opacity: 0.5;
              }
              66% {
                opacity: 0.8;
              }
              83% {
                opacity: 0.4;
              }
              100% {
                opacity: 0.7;
              }
            }
          }
        }

        .playButton {
          @apply my-6 mx-3 lg:mx-5 inline-block py-2.5 px-12 border-white/20 border-solid border rounded-lg cursor-pointer font-medium bg-[#ffcc00] text-[#272727];

          &:hover {
            @apply opacity-90;
          }
        }
      }
    }

    .advantagesSection {
      @apply relative mt-3;

      h2 {
        @apply mb-2 text-base lg:text-lg font-bold flex justify-center items-center text-white;

        &:before,
        &:after {
          @apply h-px mr-6 mt-1 flex-auto opacity-80 bg-gradient-to-r from-white/100 to-transparent content-[''];
        }

        &:after {
          @apply bg-gradient-to-l ml-6 mr-0;
        }
      }

      .advantagesGrid {
        @apply grid grid-cols-2 gap-x-5 gap-y-2;

        section {
          @apply relative my-2;
          &:nth-child(2n):before {
            @apply h-6 w-px absolute -left-1 top-1/2 -mt-1 bg-white/20 content-[''];
          }

          .icon {
            @apply text-2xl block;
          }

          p {
            @apply px-3 text-xs;
          }
        }
      }
    }
  }
}
</style>
